import Button from "@/components/button"
import CollapsibleCard from "@/components/collapsible-card"
import PickerWithSheet from "@/components/picker-with-sheet"
import Topbar from "@/components/topbar"
import { Icon } from "@/icons/icon"
import { cn } from "@/lib/utils"
import { DeptToggleGroup } from "@/packaging/components/dept-toggle-group"
import {
  ItemDetail,
  LastHistorySheet,
  V2SectionDisplayNames,
} from "@/packaging-v2/lib/schema"
import { getV2LastHistory } from "@/services/packaging-v2"
import useStore from "@/store"
import { MachineOption } from "@/store/packaging-v2"
import { LabelValue } from "@/types/common"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { format, parseISO } from "date-fns"
import { getItemSequenceLabel } from "@/packaging-v2/lib/utils"

export default function PackagingV2HistoryPage() {
  const navigate = useNavigate()
  const userStore = useStore.use.user()
  const histories = useStore.use.packagingV2().histories
  const currFormDeptId = useStore.use.packagingV2().currDeptId
  const formSchemas = useStore.use.packagingV2().formSchemas
  const [isLoading, setIsLoading] = useState(true)
  const [currDeptId, setCurrDeptId] = useState("")
  const [currMachineName, setCurrMachineName] = useState("")
  const depts = useDepts()
  const machines = useMachines()
  const { sheets, details } = useCurrSheets()

  useEffect(() => {
    async function fetchHistories() {
      setIsLoading(true)
      try {
        const deptIds = formSchemas.map((s) => s.id)
        await Promise.all(deptIds.map((id) => getV2LastHistory(id)))
      } catch (err) {
        console.error("Failed to fetch last history:", err)
      } finally {
        setIsLoading(false)
      }
    }

    void fetchHistories()
  }, [])

  function useDepts() {
    const [depts, setDepts] = useState<LabelValue[]>([])

    useEffect(() => {
      const newDepts = histories.map((history) => {
        const firstSheet = history.sheets.at(0)
        return {
          id: history.id,
          label: firstSheet?.machineName ? getDeptNameFromHistory(history.id) : history.id,
          value: history.id,
        }
      })

      setDepts(newDepts)

      if (!currDeptId) {
        const isInHistory = newDepts.find((dept) => dept.id === currFormDeptId)

        if (isInHistory) {
          setCurrDeptId(isInHistory.value)
        } else {
          setCurrDeptId(newDepts.at(0)?.value || "")
        }
      }
    }, [histories])

    return depts
  }

  function getDeptNameFromHistory(deptId: string): string {
    const packagingV2Store = useStore.getState().packagingV2
    const schema = packagingV2Store.formSchemas.find((s) => s.id === deptId)
    return schema?.name || deptId
  }

  function useMachines() {
    const [machines, setMachines] = useState<MachineOption[]>([])

    useEffect(() => {
      const dept = histories.find((item) => item.id === currDeptId)

      if (!dept) {
        setMachines([])
        return
      }

      const machineList: MachineOption[] = []
      const unique = new Set<string>()

      for (const sheet of dept.sheets) {
        if (!unique.has(sheet.machineId)) {
          unique.add(sheet.machineId)
          machineList.push({
            id: sheet.machineId,
            label: sheet.machineName,
            value: sheet.machineId,
          })
        }
      }

      setMachines(machineList)
      setCurrMachineName(machineList.at(0)?.value || "")
    }, [currDeptId])

    return machines
  }

  interface SheetDetails {
    date: string
    shift: string
    employee: string
  }

  function formatDate(timestamp: string): string {
    try {
      const date = parseISO(timestamp)
      return format(date, "dd MMMM''yy")
    } catch {
      return ""
    }
  }

  function useCurrSheets() {
    const [sheets, setSheets] = useState<LastHistorySheet[]>([])
    const [details, setDetails] = useState<SheetDetails | null>(null)

    useEffect(() => {
      const dept = histories.find((history) => history.id === currDeptId)
      if (!dept) {
        setSheets([])
        return
      }

      const machineSheets = dept.sheets.filter(
        (sheet) => sheet.machineId === currMachineName
      )

      const firstSheet = machineSheets.at(0)

      if (firstSheet) {
        setDetails({
          date: firstSheet.updatedAt ? formatDate(firstSheet.updatedAt) : "",
          shift: firstSheet.shiftName || "",
          employee: firstSheet.employeeName || "",
        })
      } else {
        setDetails(null)
      }

      setSheets(machineSheets)
    }, [currMachineName, currDeptId])

    return { sheets, details }
  }

  async function handleClickBack() {
    await navigate("/packaging-v2")
  }

  function handleChangeDept(deptId: string) {
    setCurrDeptId(deptId)
  }

  return (
    <div className="h-screen overflow-auto">
      <Topbar
        className="justify-start sticky top-0 left-0 z-10"
        title="Last History Sheet"
        subtitle={userStore.plant.name}
        slotSide="left"
      >
        <Button className="mr-4" variant="ghost" onClick={handleClickBack}>
          <Icon name="ArrowRight" className="h-6 w-auto rotate-180" />
        </Button>
      </Topbar>

      <div className="mt-4 px-4">
        {isLoading ? (
          <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
            <div className="text-label-text text-lg">Loading history...</div>
          </div>
        ) : (
        <>
        <DeptToggleGroup
          className="mb-6 mx-auto"
          type="single"
          options={depts}
          defaultValue={depts.at(0)?.value}
          value={currDeptId}
          onValueChange={handleChangeDept}
        />

        {!!machines.length && (
          <PickerWithSheet
            className="mb-4"
            options={machines}
            value={currMachineName}
            deptId={currDeptId}
            indicators={{}}
            onChange={setCurrMachineName}
          />
        )}

        {sheets.length ? (
          <div>
            {sheets.map((sheet) => (
              <div key={sheet.sheetId} className="space-y-4">
                {sheet.items.map((item) => (
                  <ItemCard key={item.id} item={item} />
                ))}
              </div>
            ))}

            {!!details && (
              <div className="flex flex-wrap gap-2 items-center text-label-text text-sm font-medium mt-4 pb-4">
                <div>Date: {details.date}</div>
                <div className="text-icon">•</div>
                <div>Shift: {details.shift}</div>
                {details.employee && (
                  <>
                    <div className="text-icon">•</div>
                    <div>By: {details.employee}</div>
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="text-label-text flex justify-center mt-8">
            No sheets submitted
          </div>
        )}
        </>
        )}
      </div>
    </div>
  )
}

interface ItemCardProps {
  item: ItemDetail
}

function ItemCard({ item }: ItemCardProps) {
  const title = `${getItemSequenceLabel(item.sequenceOrder)} - ${item.productName} (${item.skuCode})`

  return (
    <CollapsibleCard title={title}>
      {/* Item Info Header */}
      <div className="bg-band-fill py-2.5 px-3">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-label-text">Market: </span>
            <span className="font-medium">{item.marketType}</span>
          </div>
          <div>
            <span className="text-label-text">SKU: </span>
            <span className="font-medium">{item.skuName}</span>
          </div>

        </div>
      </div>

      {/* Sections */}
      {item.sections.map((sectionData) => (
        <div key={sectionData.section}>
          <h3
            className={cn(
              "flex justify-between items-center py-2.5 px-3",
              "bg-band-fill uppercase text-sm font-semibold mt-2"
            )}
          >
            {V2SectionDisplayNames[sectionData.section]}
          </h3>

          <div className="grid grid-cols-2 gap-y-4 gap-x-1 px-4 pb-4 mt-4">
            {sectionData.readings.map((reading) => (
              <div key={reading.id} className="flex flex-col gap-1 col-span-1">
                <h4 className="text-label-text text-sm font-medium">
                  {reading.parameterName}
                  {reading.unit && ` (${reading.unit})`}
                </h4>
                <div className="text-base font-medium text-button-black">
                  {reading.windowReadings && reading.windowReadings.length > 0 ? (
                    <WindowReadingsDisplay readings={reading.windowReadings} />
                  ) : (
                    <ValueDisplay
                      value={reading.value}
                      validationStatus={reading.validationStatus}
                      minLimit={reading.minLimit}
                      maxLimit={reading.maxLimit}
                    />
                  )}
                </div>
                {reading.remark && (
                  <div className="text-xs text-label-text italic">
                    Note: {reading.remark}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </CollapsibleCard>
  )
}

interface ValueDisplayProps {
  value: string
  validationStatus?: "VALID" | "BELOW_LIMIT" | "ABOVE_LIMIT"
  minLimit?: number
  maxLimit?: number
}

function ValueDisplay({ value, validationStatus, minLimit, maxLimit }: ValueDisplayProps) {
  const hasLimits = minLimit !== undefined || maxLimit !== undefined

  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          validationStatus === "BELOW_LIMIT" && "text-red-500",
          validationStatus === "ABOVE_LIMIT" && "text-red-500"
        )}
      >
        {value}
      </span>
      {hasLimits && (
        <span className="text-xs text-label-text">
          ({minLimit ?? "-"} - {maxLimit ?? "-"})
        </span>
      )}
    </div>
  )
}

interface WindowReadingsDisplayProps {
  readings: Array<{
    id: string
    value: string
    startTime: string
    endTime: string
    overtime: boolean | null
    early: boolean | null
  }>
}

function WindowReadingsDisplay({ readings }: WindowReadingsDisplayProps) {
  function formatWindowTime(isoTime: string): string {
    try {
      const date = parseISO(isoTime)
      return format(date, "HH:mm")
    } catch {
      return ""
    }
  }

  return (
    <div className="flex flex-col gap-1">
      {readings.map((reading) => (
        <div
          key={reading.id}
          className={cn(
            "flex items-center gap-2 text-sm",
            reading.overtime && "text-orange-500",
            reading.early && "text-blue-500"
          )}
        >
          <span className="text-label-text text-xs">
            {formatWindowTime(reading.startTime)}-{formatWindowTime(reading.endTime)}:
          </span>
          <span className="font-medium">{reading.value}</span>
          {reading.overtime && (
            <span className="text-xs text-orange-500">(overtime)</span>
          )}
          {reading.early && (
            <span className="text-xs text-blue-500">(early)</span>
          )}
        </div>
      ))}
    </div>
  )
}
